import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';

import { tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Validators } from 'angular-reactive-validation';

import { Hub } from '@qwyk/models';
import { CanComponentDeactivate, MasterDataService } from '@qwyk/core';
import { PortalTeamsFacade } from '@qwyk/hub/administration/data-access/portal-teams';
import { CustomerUsersService } from '@qwyk/hub/administration/data-access/customer-users';

@Component({
    selector: 'qwyk-customer-user-create',
    templateUrl: './customer-user-create.component.html',
    styleUrls: ['./customer-user-create.component.scss'],
})
export class CustomerUserCreateComponent
    implements CanComponentDeactivate, OnDestroy
{
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly translate = inject(TranslateService);
    private readonly masterData = inject(MasterDataService);
    private readonly portalTeamsFacade = inject(PortalTeamsFacade);
    private readonly customerUsersService = inject(CustomerUsersService);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Input() public error: any;
    @Output()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public save: EventEmitter<any> = new EventEmitter();
    public form: FormGroup = this.fb.group({
        email: [
            null,
            [
                Validators.required(
                    this.translate.instant(
                        'common.validation.enter-an-email-address'
                    )
                ),
                Validators.email(
                    this.translate.instant(
                        'common.validation.enter-a-valid-email-address'
                    )
                ),
                Validators.maxLength(191, (maxLength) =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        password: [
            { value: this.generateRandomPassword(), disabled: true },
            [
                Validators.required(
                    this.translate.instant('common.validation.enter-a-password')
                ),
                Validators.minLength(12, minLength =>
                    this.translate.instant('common.validation.min-length', {
                        field: this.translate
                            .instant('common.password')
                            .toLowerCase(),
                        minLength,
                    })
                ),
                Validators.maxLength(36, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength }
                    )
                ),
                Validators.pattern(
                    /^[a-zA-Z0-9!@#$%&\-.*=]+$/,
                    this.translate.instant(
                        'common.validation.pattern',
                        this.translate.instant('common.password').toLowerCase()
                    )
                ),
            ],
        ],
        team_id: [
            null,
            [
                Validators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.select-a-customer-from-the-list'
                    )
                ),
            ],
        ],
        registered_site_id: [
            null,
            [
                Validators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.select-a-site-from-the-list'
                    )
                ),
            ],
        ],
        restrict_to_registered_site: [false],
        name: [
            null,
            [
                Validators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.enter-a-name'
                    )
                ),
                Validators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        company_name: [
            null,
            [
                Validators.required(
                    this.translate.instant(
                        'hub.administration.portal-users.enter-a-company-name'
                    )
                ),
                Validators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        job_title: [
            null,
            [
                Validators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        location: [
            null,
            [
                Validators.maxLength(191, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        country_code: [null],
        phone_number: [
            null,
            [
                Validators.maxLength(20, maxLength =>
                    this.translate.instant(
                        'common.validation.max-length',
                        { maxLength },
                    )
                ),
            ],
        ],
        marketing_optin: false,
        privacy_compliance: [
            false,
            [
                Validators.requiredTrue(
                    this.translate.instant(
                        'hub.administration.portal-users.you-must-confirm-this-action'
                    )
                ),
            ],
        ],
    });

    public loadingSites = true;
    public loadingCountries = true;
    public pageSize = 25;
    public complianceTooltipText = this.translate.instant(
        'hub.administration.portal-users.compliance-tooltip-text'
    );

    public loadingTeams$: Observable<boolean> = this.portalTeamsFacade.loading$;
    public teams$: Observable<Hub.CustomerTeam[]> = this.portalTeamsFacade.allPortalTeams$.pipe(
        tap(teams => {
            if (
                teams &&
                teams.length === 1 &&
                !this.form.get('team_id').value
            ) {
                this.form.get('team_id').setValue(teams[0].id);
                this.form.get('company_name').setValue(teams[0].name);
            }
        })
    );

    public sites$ = this.customerUsersService
        .getSites({ pageSize: 100000, active: true })
        .pipe(
            tap(sites => {
                this.loadingSites = false;
                if (
                    sites &&
                    sites.length === 1 &&
                    !this.form.get('registered_site_id').value
                ) {
                    this.form.get('registered_site_id').setValue(sites[0].id);
                }
            })
        );
    public countries$ = this.masterData
        .getCountries()
        .pipe(tap(() => (this.loadingCountries = false)));

    private paramsSubscription: Subscription;

    public get iconAnimation(): 'pulse' | undefined {
        return this.form.disabled ? 'pulse' : undefined;
    }

    constructor() {
        this.paramsSubscription = this.route.queryParamMap.subscribe(params => {
            if (
                params.has('team_id') &&
                this.form.get('team_id') &&
                !this.form.get('team_id').value
            ) {
                this.form.get('team_id').setValue(params.get('team_id'));
            }
        });
        this.portalTeamsFacade.loadPortalTeams({ page: 1, limit: this.pageSize });
    }

    private _saving: boolean;

    public get saving(): boolean {
        return this._saving;
    }

    @Input()
    public set saving(saving: boolean) {
        this._saving = saving;
        setTimeout(() => {
            if (saving) {
                this.form.disable();
            } else {
                this.form.enable();
                this.form.get('password').disable();
            }
        }, 0);
    }

    ngOnDestroy(): void {
        this.paramsSubscription.unsubscribe();
    }

    public onCreate() {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.form.markAsPristine();
        this.save.emit(this.form.getRawValue());
    }

    public onSetRandomPassword() {
        this.form.get('password').setValue(this.generateRandomPassword());
    }

    public canDeactivate() {
        if (this.form.dirty) {
            return confirm(
                this.translate.instant(
                    'portal.settings.profile.unsaved-changes'
                )
            );
        }
        return true;
    }

    public onTeamSelected(team) {
        if (team) {
            if (!this.form.get('company_name').touched && team.name) {
                this.form.get('company_name').setValue(team.name);
            }
            if (!this.form.get('location').touched) {
                this.form.get('location').setValue(team.city);
            }
            if (!this.form.get('country_code').touched) {
                this.form.get('country_code').setValue(team.country_code);
            }
        }
    }

    public onSearchTeams(term: string): void {
        this.portalTeamsFacade.loadPortalTeams({ name: term })
    }

    public onClearTeamsSearch(): void {
        this.portalTeamsFacade.loadPortalTeams({ page: 1, limit: this.pageSize });
    }

    private generateRandomPassword(minLength = 12, maxLength = 18): string {
        const length =
            minLength + Math.floor((maxLength - minLength + 1) * Math.random());
        const chars =
            'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ0123456789!!@@##$$';
        let pwd = '';
        for (let i = 0; i < length; i++) {
            let newChar = null;
            while (newChar === null || pwd.includes(newChar)) {
                newChar = chars[Math.floor(Math.random() * chars.length)];
            }
            pwd += newChar;
        }

        return pwd;
    }
}
